import * as THREE from 'three';
import type { Fetch } from './Fetch';
import type { Dog } from '../dog/Dog';

/**
 * Yard games with the toy: a cornhole board (3 for the hole, 1 for landing on
 * the board) and cans on the fence posts (1 each; they reset after a while).
 */
export interface GameEvent {
  kind: 'hole' | 'board' | 'can';
  points: number;
  text: string;
}

const G = 9.81;
const _l = new THREE.Vector3();
const _n = new THREE.Vector3();
const _v = new THREE.Vector3();
const _up = new THREE.Vector3(0, 1, 0);

export class Games {
  onScore?: (e: GameEvent) => void;
  session = 0;
  private board = new THREE.Group();
  private boardInv = new THREE.Matrix4();
  private boardW = 0.6;
  private boardL = 1.2;
  private boardTilt = 0.32; // rad
  private holeR = 0.11;
  private holeLocalZ = -0.32; // toward the high end
  private lastBoardHit = 0;
  private cans: { mesh: THREE.Mesh; home: THREE.Vector3; vel: THREE.Vector3; down: boolean; t: number }[] = [];

  constructor(
    scene: THREE.Object3D,
    private fetch: Fetch,
    private dog: Dog,
    boardPos: THREE.Vector3,
    boardYaw: number,
    postTops: THREE.Vector3[],
  ) {
    // --- cornhole board: a tilted plank on a leg, hole drawn as a dark disc
    const wood = new THREE.MeshStandardMaterial({ color: 0xd9b27c, roughness: 0.9 });
    const plank = new THREE.Mesh(new THREE.BoxGeometry(this.boardW, 0.04, this.boardL), wood);
    plank.castShadow = true;
    plank.receiveShadow = true;
    const hole = new THREE.Mesh(new THREE.CircleGeometry(this.holeR, 24), new THREE.MeshStandardMaterial({ color: 0x1b1612, roughness: 1 }));
    hole.rotation.x = -Math.PI / 2;
    hole.position.set(0, 0.021, this.holeLocalZ);
    const ring = new THREE.Mesh(new THREE.RingGeometry(this.holeR, this.holeR + 0.02, 24), new THREE.MeshStandardMaterial({ color: 0x7b5cc4, roughness: 0.8 }));
    ring.rotation.x = -Math.PI / 2;
    ring.position.set(0, 0.022, this.holeLocalZ);
    const tilt = new THREE.Group();
    tilt.add(plank, hole, ring);
    tilt.rotation.x = this.boardTilt; // high end at -z (local), low end toward +z / the thrower
    tilt.position.y = 0.02 + (this.boardL / 2) * Math.sin(this.boardTilt);
    const leg = new THREE.Mesh(new THREE.BoxGeometry(this.boardW * 0.9, (this.boardL - 0.1) * Math.sin(this.boardTilt), 0.04), wood);
    leg.position.set(0, ((this.boardL - 0.1) * Math.sin(this.boardTilt)) / 2, -this.boardL / 2 + 0.1);
    leg.castShadow = true;
    this.board.add(tilt, leg);
    this.board.position.copy(boardPos);
    this.board.rotation.y = boardYaw;
    this.board.name = 'cornhole';
    this.board.userData.tapName = 'cornhole';
    scene.add(this.board);
    this.board.updateMatrixWorld(true);
    this.boardInv.copy(tilt.matrixWorld).invert();

    // --- cans on the posts
    const canMat = new THREE.MeshStandardMaterial({ color: 0xd0d4d8, roughness: 0.35, metalness: 0.7 });
    for (const top of postTops) {
      const mesh = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 0.13, 14), canMat);
      const stripe = new THREE.Mesh(new THREE.CylinderGeometry(0.051, 0.051, 0.05, 14), new THREE.MeshStandardMaterial({ color: 0xd0342c, roughness: 0.6 }));
      mesh.add(stripe);
      mesh.castShadow = true;
      mesh.userData.tapName = 'can';
      const home = top.clone().add(new THREE.Vector3(0, 0.065, 0));
      mesh.position.copy(home);
      scene.add(mesh);
      this.cans.push({ mesh, home, vel: new THREE.Vector3(), down: false, t: 0 });
    }
  }

  get boardPosition(): THREE.Vector3 {
    return this.board.position;
  }

  /** things you can tap to go and play */
  get pickables(): THREE.Object3D[] {
    return [this.board, ...this.cans.map((c) => c.mesh)];
  }

  /** where to stand to throw at the board: a few metres off its low end, and where to look */
  tee(out: THREE.Vector3, distance = 3.2): THREE.Vector3 {
    return out.set(0, 0, distance).applyAxisAngle(_up, this.board.rotation.y).add(this.board.position);
  }

  /** the middle of the can line (fence top), for facing the cans */
  cansCentre(out: THREE.Vector3): THREE.Vector3 {
    out.set(0, 0, 0);
    for (const c of this.cans) out.add(c.home);
    return out.divideScalar(Math.max(1, this.cans.length));
  }

  update(dt: number): void {
    const toy = this.fetch.root;
    const loose = toy.visible && !this.fetch.carried && !this.fetch.inHand;
    const vel = this.fetch.velocity;
    const r = this.fetch.kind === 'ball' ? 0.065 : 0.024;

    // --- cornhole: check the toy against the tilted plank in its local frame
    if (loose) {
      _l.copy(toy.position).applyMatrix4(this.boardInv); // plank local: y up from the surface, z along the plank
      const onPlank = Math.abs(_l.x) < this.boardW / 2 + r && Math.abs(_l.z) < this.boardL / 2 + r;
      if (onPlank && _l.y < r + 0.02 && _l.y > -0.15) {
        const dHole = Math.hypot(_l.x, _l.z - this.holeLocalZ);
        if (dHole < this.holeR - r * 0.3 && this.fetch.kind === 'ball') {
          // through the hole: drop it under the board and stop it
          _v.copy(toy.position);
          toy.position.set(_v.x, r, _v.z);
          vel.set(0, 0, 0);
          this.score({ kind: 'hole', points: 3, text: 'in the hole! +3' });
          this.dog.excitement = 1;
        } else if (performance.now() - this.lastBoardHit > 400) {
          // bounce off the plank: normal is the plank's local +y in world
          _n.set(0, 1, 0).transformDirection(this.board.children[0].matrixWorld);
          const vn = vel.dot(_n);
          if (vn < 0) {
            vel.addScaledVector(_n, -(1 + 0.35) * vn);
            vel.multiplyScalar(0.8);
            toy.position.addScaledVector(_n, r + 0.02 - _l.y);
            this.lastBoardHit = performance.now();
            if (Math.abs(vn) > 1.2) {
              this.dog.sounds.play('thud', 0.4);
              this.score({ kind: 'board', points: 1, text: 'on the board +1' });
            }
          }
        }
      }
    }

    // --- cans
    for (const c of this.cans) {
      if (!c.down) {
        if (loose && vel.lengthSq() > 1 && toy.position.distanceTo(c.mesh.position) < r + 0.09) {
          c.down = true;
          c.t = 0;
          c.vel.copy(vel).multiplyScalar(0.5).add(_v.set((Math.random() - 0.5) * 1.5, 2.2, (Math.random() - 0.5) * 1.5));
          this.dog.sounds.play('thud', 0.7);
          const n = this.cans.filter((x) => x.down).length;
          this.score({ kind: 'can', points: 1, text: `can down! ${n}/${this.cans.length} +1` });
          this.dog.notice(c.mesh.position);
        }
        continue;
      }
      // falling / lying can
      c.t += dt;
      if (c.mesh.position.y > 0.07 || c.vel.y > 0) {
        c.vel.y -= G * dt;
        c.mesh.position.addScaledVector(c.vel, dt);
        c.mesh.rotation.x += 6 * dt;
        c.mesh.rotation.z += 4 * dt;
        if (c.mesh.position.y <= 0.07) {
          c.mesh.position.y = 0.05;
          c.vel.set(0, 0, 0);
          c.mesh.rotation.set(Math.PI / 2, c.mesh.rotation.y, 0);
        }
      }
      if (c.t > 25) {
        c.down = false;
        c.mesh.position.copy(c.home);
        c.mesh.rotation.set(0, 0, 0);
      }
    }
  }

  private score(e: GameEvent): void {
    this.session += e.points;
    this.onScore?.(e);
  }
}
